import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from "@mui/material/Typography";

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Bs4Popper from '../../Bs4Components/Bs4Popper';
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';

import * as Bs4Utils from '../../Bs4Components/Bs4Utils'
import { getBoxSx, getBoxStyle } from '../../Bs4Theme/Bs4UxTemplates';
import { SmallScreenContext } from '../../Bs4Theme/Bs4UxTemplates';
import { useDraggable } from '@dnd-kit/core';

import {  
  startOfDay, endOfDay,
  compareAsc,
  format,
  differenceInMilliseconds,
  addMilliseconds,
} from "date-fns";

import { VIEW_TYPE_DAY, eventHeight, borderWidth } from './Bs4ReportCalendarConst';
import { v4 as uuidv4 } from 'uuid';
 
const svgBan =  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path fill="red" d="M367.2 412.5L99.5 144.8C77.1 176.1 64 214.5 64 256c0 106 86 192 192 192c41.5 0 79.9-13.1 111.2-35.5zm45.3-45.3C434.9 335.9 448 297.5 448 256c0-106-86-192-192-192c-41.5 0-79.9 13.1-111.2 35.5L412.5 367.2zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"/></svg>';

const DRAGGABLE = 'draggable_';
export const ORIENTATION_WIDTH = 'width';
export const ORIENTATION_HEIGHT = 'height';
export const ORIENTATION_LIST = 'list';

const borderMain = {
  borderWidth: 0.2,
  borderStyle: 'solid',
  // borderColor: '#00000066',
  borderRadius: 1,
  ///boxShadow: '0px 0px 0px 1px rgba(255, 255, 255, 1)',
}

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
)) (({ theme, boxsx}) => { 
  return({
  [`& .${tooltipClasses.tooltip}`]: {
    ...boxsx,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: boxsx?.backgroundColor,
    "&::before": {
        ...boxsx,
        borderRadius: '0px',
      }
  },
})});

const HtmlTooltipCanOpen = (props) => {
  const {canOpen, ...other} = props;
  const [open, setOpen] = React.useState(false)

  const handleOpen = (event) => {
    setOpen(canOpen);
  }

  const handleClose = (event) => {
    setOpen(false);
  }

  return (
    <HtmlTooltip
      open={open && canOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      {...other}
    >
      {props.children}
    </HtmlTooltip>
  );
}

const maouseOverBorder = (node, event, delta, orientation) => {
  const rect = node.getBoundingClientRect();
  const x = event.clientX - rect.left,      // the relative mouse postion to the element
        y = event.clientY - rect.top,       // ...
        w = rect.right - rect.left,     // width of the element
        h = rect.bottom - rect.top;     // height of the element
      
 let c = "";                          // which cursor to use
 
 if (orientation === ORIENTATION_HEIGHT) {
  if(y < delta) c += "n";              // north
  else if( y > h - delta) c += "s";    // south
 }

 if (orientation === ORIENTATION_WIDTH || orientation === ORIENTATION_LIST) {
  if(x < delta) c += "w";              // west
  else if(x > w - delta) c += "e";     // east
 }

 return c;
}

export const ViewBs4EventDragOverlay = (props) => {
  const [anchorPosition, setAnchorPosition] = React.useState(null);
  const theme = useTheme();

  const canDragBs4Event = props.el.bs4Event.chtime && !props.noChtime;

  const boxSx = React.useMemo(() => {          
    return {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      ...getBoxStyle(borderMain, theme),
      ...getBoxStyle(props.uxBodyEvent, theme),
      ...getBoxStyle(props.el?.bs4Event?.xx555, theme),
      boxShadow: theme.shadows[5],
      opacity: 0.5,
    };
  }, [theme, props.el?.bs4Event?.xx555]);

  React.useEffect(() => {
    const handlePointerMove = (event) => {
      if (event.pointerType === 'touch') return;   
      setAnchorPosition({ top: event.clientY, left: event.clientX });      
    }

    const handleTouchMove = (event) => {      
      const touch = event.touches[0];
      setAnchorPosition({ top: touch.clientY, left: touch.clientX });      
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener('touchmove', handleTouchMove);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener('touchmove', handleTouchMove);
    }
  }, [setAnchorPosition]);

  const [moveIcon, menuOptions, startIcon, endIcon] = React.useMemo(() => {
    const moveAll = <i className="fa-regular fa-arrows-up-down-left-right"></i>
    const resizeLeft = <i className="fa-regular fa-arrow-left-from-line"></i>;
    const resizeRight = <i className="fa-regular fa-arrow-right-from-line"></i>
    const resizeUp = <i className="fa-regular fa-arrow-up-from-line"></i>
    const resizeDown = <i className="fa-regular fa-arrow-down-from-line"></i>
    const menuOptions = props.el.bs4Event.chaxis && !props.el.bs4Event?.move?.cursor ? <i className="fa-regular fa-ellipsis"></i> : null;

    let moveIcon = null;
    let startIcon = null;
    let endIcon = null;

    if (canDragBs4Event) switch (props.el.bs4Event?.move?.cursor) {
        case 'n': 
          startIcon = resizeUp;
          break;
        case 's': 
          endIcon = resizeDown;
          break;
        case 'w':  
          startIcon = resizeLeft;
          break;
        case 'e':  
          endIcon = resizeRight;
          break;
        default: 
          moveIcon = moveAll;
          break;
    }

    return [moveIcon, menuOptions, startIcon, endIcon];
  }, [props.el.bs4Event?.move?.cursor, canDragBs4Event, props.el.bs4Event.chaxis])

  const [startTime, endTime] = React.useMemo(() => {
    return [
      canDragBs4Event ? <Typography variant='body2'>{Bs4Utils.toDateMinutes(props.el.bs4Event.start)}</Typography> : null, 
      canDragBs4Event ? <Typography variant='body2'>{Bs4Utils.toDateMinutes(props.el.bs4Event.end)}</Typography> : null,
    ];
  }, [canDragBs4Event, props.el.bs4Event.start, props.el.bs4Event.end]);
    
  return (
    <Popover
      disableEscapeKeyDown
      open={Boolean(anchorPosition)}        
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      anchorOrigin={{vertical: 'top', horizontal: 'left',}}
      transformOrigin={{vertical: 'bottom', horizontal: 'left',}}
      slotProps={{paper: {sx: {backgroundColor: 'transparent'}}}}
      sx={{cursor: props.el?.bs4Event?.move?.visibleCursor || null,}}
    >      
    <Stack 
      direction="column"
      spacing={0} 
      sx={{
        justifyContent: "flex-start", 
        alignItems: "flex-start", 
        ...boxSx,
        padding: 1,
        wordBreak: 'keep-all',
        fontSize: 10,
      }}
    >        
      <Stack direction="row" spacing={1} sx={{justifyContent: "flex-start", alignItems: "flex-start",}}>
        {moveIcon} {menuOptions} {startIcon}
      </Stack>
      {startTime} {endTime} {endIcon}
    </Stack>
    </Popover>
  )
}

export const ViewBs4EventDraggable = React.memo((props) => {
  const setActive = React.useCallback((dragging) => {
    props.el.bs4Event.move.active = dragging;
    if (props?.onClose) props.onClose();
  }, [props.el.bs4Event]);
    
  // Zachowuje key dla el przeciąganego. Zapewnia poprawną animację.
  // ?????????????????? ?????????????????? ??????????????
  if (props.el.bs4Event?.move?.moveDate && props.el.bs4Event?.move?.key &&
    (compareAsc(props.el.bs4Event.move.moveDate, props.el.start) >= 0) && 
    (compareAsc(props.el.end, props.el.bs4Event.move.moveDate) >= 0)) {      
    props.el.key = props.el.bs4Event.move.key;
  } else if (!props.el.key) props.el.key = uuidv4();

  const canDragBs4Event = props.el.bs4Event.chtime && !props.noChtime;

  const {
    setNodeRef, 
    setActivatorNodeRef,
    listeners, 
    // isDragging,
    // node,
    // over,
  } = useDraggable({
    id: props.el.bs4Event.move?.active ? null : DRAGGABLE+props.el.key,
    disabled: props.el.bs4Event.layer || (!canDragBs4Event && !props.el?.bs4Event?.chaxis),
    data: {      
      props: props,
      setActive: setActive,
    },
  });

  return (      
    <ViewBs4EventEl
      {...props}
      setNodeRef={setNodeRef} 
      setActivatorNodeRef={setActivatorNodeRef} 
      listeners={listeners}
    />
  )
});

export const ViewBs4EventEl = React.memo((props) => {
  const theme = useTheme();  

  // window.oncontextmenu = function() { return false; }
  // window.addEventListener("contextmenu", function(e) { e.preventDefault(); })

  // const smallScreen = React.useContext(SmallScreenContext);
  
  const [select, setSelect] = React.useState(false);
  const [cursor, setCursor] = React.useState(null);

  const canDragToOptions = Boolean(props.el?.bs4Event?.chaxis);
  const canDragBs4Event = Boolean(props.el.bs4Event.chtime && !props.noChtime);
  const canDrag = !props.el?.bs4Event?.layer && (canDragToOptions || canDragBs4Event);

  const [startSize, endSize]  = React.useMemo(() => {  
    return [compareAsc(props.el.bs4Event.start, props.el.start) >= 0, compareAsc(props.el.end, props.el.bs4Event.end) >= 0]    
  }, [props.el.bs4Event.start, props.el.start, props.el.bs4Event.end, props.el.end])

  const widthResizeHolder = 5;
  const setNewCursor = (cursor) => {
    if (canDrag) if (
      (props.orientation === ORIENTATION_HEIGHT && ((startSize && cursor === 'n') || (endSize && cursor === 's'))) ||
      (props.orientation === ORIENTATION_WIDTH && ((startSize && cursor === 'w') || (endSize && cursor === 'e')))
    ) {
      setCursor(cursor);
      return cursor;
    }
    
    setCursor(null);
    return null;
  }
    
  const ref = React.useRef(null);

  const enableDrop = true;

  const boxSx = React.useMemo(() => {          
    const updateSx = {};
    if (!startSize) {
      if (props.orientation === ORIENTATION_HEIGHT) {
        updateSx.borderTopLeftRadius = '0px';
        updateSx.borderTopRightRadius = '0px';
        updateSx.borderTopStyle = "none";
      } else if (props.orientation === ORIENTATION_WIDTH || props.orientation === ORIENTATION_LIST) {
        updateSx.borderTopLeftRadius = '0px';
        updateSx.borderBottomLeftRadius = '0px';
        updateSx.borderLeftStyle = "none";
      }       
    }
    if (!endSize) {
      if (props.orientation === ORIENTATION_HEIGHT) {
        updateSx.borderBottomLeftRadius = '0px';
        updateSx.borderBottomRightRadius = '0px';
        updateSx.borderBottomStyle = "none";
      } else if (props.orientation === ORIENTATION_WIDTH || props.orientation === ORIENTATION_LIST) {
        updateSx.borderTopRightRadius = '0px';
        updateSx.borderBottomRightRadius = '0px';
        updateSx.borderRightStyle = "none";
      }
    }

    if (props.over) {
      updateSx.opacity = props.el.bs4Event?.move?.cursor ? 0 : 0.33;
    }

    // if (props.over && !enableDrop) {
    //   updateSx['&::after'] = {          
    //     content: '""',
    //     backgroundImage: `url('data:image/svg+xml;utf8,${svgBan}')`,
    //     backgroundRepeat: 'no-repeat',
    //     backgroundOrigin: 'content-box',
    //     backgroundColor: '#ffffff88',
    //     position: 'absolute',
    //     top: 0,
    //     left: 0,
    //     padding: '4px',
    //     height: '100%',
    //     width: '100%',
    //   }
    // }
    
    if (props.el.bs4Event.move?.active || select) {
      updateSx.boxShadow = theme.shadows[5];
    }

    if (props.el?.bs4Event?.new) {
      updateSx.opacity = 0.5;
    }

    return {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      ...getBoxStyle(borderMain, theme),
      ...getBoxStyle(props.uxBodyEvent, theme),
      ...getBoxStyle(props.el?.bs4Event?.xx555, theme),
      ...getBoxStyle(updateSx, theme),      
      overflow: props.orientation === ORIENTATION_LIST ? null : 'hidden',
    };
  }, [props.uxBodyEvent, props.el.bs4Event?.move?.cursor ,props.el.bs4Event.xx555, props.orientation, props.over, theme, select]);    

  const handleRowClick = (event) => {            
    if (props.onRowClick) props.onRowClick(props.el.bs4Event);
    if (props?.onClose) props.onClose();      
  }

  const TooltipView = React.useMemo(() => {   
    return (
      <Stack direction="column" spacing={0} sx={{justifyContent: "flex-start", alignItems: "flex-start",}}>      
        <Typography
          dangerouslySetInnerHTML={{__html: props.el.bs4Event.bs4fill2 || props.el.bs4Event.Name}}
          onClick={handleRowClick}
        />
        {/* <Typography >{`od:${Bs4Utils.toDateTime(props.el.bs4Event.start)} do:${Bs4Utils.toDateTime(props.el.bs4Event.end)}`}</Typography>  */}
      </Stack>
    )
  }, [props.el.bs4Event.bs4fill2, props.el.bs4Event.Name]);

  const updateMove = React.useCallback((event, cursor) => {    
    props.el.bs4Event.move = {      
      key: props.el.key,      
      start: props.el.bs4Event.start,
      end: props.el.bs4Event.end,
      duration: props.el.bs4Event.duration,
      moveDate: null,
      cursor: null,
    };    

    const rect = ref.current.getBoundingClientRect();
    const differenceInMillisecondsStartToEnd = differenceInMilliseconds(props.el.end, props.el.start);    
    const size = props.orientation === ORIENTATION_HEIGHT ? rect.height : rect.width;
    const offsetFromOrigin = props.orientation === ORIENTATION_HEIGHT ? (event.pageY - rect.top) : (event.pageX - rect.left);
    const offsetFromOriginInMilliseconds = (differenceInMillisecondsStartToEnd / size) * offsetFromOrigin;    
    // moveDate = data cursora (miejsce kursora w momencie chwytu)
    props.el.bs4Event.move.moveDate = addMilliseconds(props.el.start, offsetFromOriginInMilliseconds);
    
    props.el.bs4Event.move.cursor = cursor;

    if (cursor) {
      if (cursor === 'n' || cursor === 'w') props.el.bs4Event.move.moveDate = props.el.bs4Event.start;
      if (cursor === 's' || cursor === 'e') props.el.bs4Event.move.moveDate = props.el.bs4Event.end;    
    }

  }, [props.el, props.orientation, ref]);

  const handleMove = (event) => {
    if (props.el.bs4Event?.move?.active) return;
    setNewCursor(maouseOverBorder(ref.current, event, 5, props.orientation));
  }

  const handlePointerDown = React.useCallback(event => {
    event.stopPropagation();
    if (props.el.bs4Event?.move?.active) return;
    if (!canDrag) return;
    const cursor = setNewCursor(maouseOverBorder(ref.current, event, widthResizeHolder, props.orientation));
    updateMove(event, cursor);
    props.listeners?.onPointerDown(event);
  }, [props.orientation, props.listeners, ref.current, canDrag, maouseOverBorder, setNewCursor, updateMove]);

  const handleTouchStart = React.useCallback(event => {
    event.stopPropagation();        
  }, [props.orientation, props.listeners, ref.current, canDrag, maouseOverBorder, setNewCursor, updateMove]);

  const handleSelect =  React.useCallback(event => {
    if (!canDrag) return;
    if (event.pointerType === 'touch') return;
    if (props.el.bs4Event?.move?.active) return;
    if (props.el?.bs4Event?.new) return;    
    setSelect(event.type === 'pointerenter');
  }, [setSelect, canDrag]);

  const cursorSx = React.useMemo(() => {
    const cursorCss = {};
    
    if (!canDrag) return cursorCss; 
    if (!select && !props.el.bs4Event.move?.active) return cursorCss;

    const activeCursor = props.el.bs4Event.move?.active ? props.el.bs4Event.move.cursor : null;
    const currentCursor = activeCursor || cursor || null;    
    const border = 'double';    
    
    if (canDragBs4Event) {
      switch (currentCursor) {
      case 'n':
        if (startSize) {
          cursorCss.cursor = 'ns-resize';
          cursorCss.borderTopStyle = border;
          cursorCss.borderTopWidth = widthResizeHolder;
        }
        break;

      case 's':
        if (endSize) {
          cursorCss.cursor = 'ns-resize';
          cursorCss.borderBottomStyle = border;
          cursorCss.borderBottomWidth = widthResizeHolder;
        }
        break;

      case 'w':        
        if (startSize) {
          cursorCss.cursor = 'ew-resize';
          cursorCss.borderLeftStyle = border;
          cursorCss.borderLeftWidth = widthResizeHolder;
        }
        break;

      case 'e':
        if (endSize) {
          cursorCss.cursor = 'ew-resize';
          cursorCss.borderRightStyle = border;
          cursorCss.borderRightWidth = widthResizeHolder;
        }
        break;

      default:
        if (props.el.bs4Event.move?.active) cursorCss.cursor = 'grabbing';
        else cursorCss.cursor = 'grab';
        break;
      }
    } else {
      if (props.el.bs4Event.move?.active) cursorCss.cursor = 'grabbing';
      else cursorCss.cursor = 'grab';
    }

    if (props.el.bs4Event?.move) props.el.bs4Event.move.visibleCursor = cursorCss.cursor;
    return cursorCss;
  }, [cursor, select, startSize, endSize, canDragBs4Event, props.el.bs4Event?.move?.cursor,  props.el.bs4Event?.move?.active])  

  const canOpenTooltip = !(Boolean(props.noTooltip) || Boolean(props.el.bs4Event?.move?.active) || Boolean(props.el?.bs4Event?.new));

  return (     
    <HtmlTooltipCanOpen
      boxsx={{
        ...boxSx,
        boxShadow: theme.shadows[3],
        overflow: 'inherit',
      }}
      canOpen={canOpenTooltip}
      arrow
      placement={props.tooltipPlacement ? props.tooltipPlacement : 'top'}
      followCursor={Boolean(props.followCursor)}
      title={TooltipView || null}
      sx={{opacity: props.over ? 0.5 : 'unset'}}      
    >
      <Box
        ref={node => {
          if (props.setNodeRef) props.setNodeRef(node);
          ref.current = node;          
        }} 
        onClick={handleRowClick}
        sx={{
          ...boxSx, 
          ...props.sx,
          ...cursorSx, 
          position: 'relative',
          userSelect: 'none',     
          height: '100%',
          touchAction: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handleMove}
        onPointerEnter={handleSelect}
        onPointerLeave={handleSelect}    
        onTouchStart={handleTouchStart}
      >       
        {props.el?.bs4Event?.new ? null : <Typography variant='subtitle2' dangerouslySetInnerHTML={{__html: props.el.bs4Event.Name}}/>}
      </Box>    
    </HtmlTooltipCanOpen>   
  );
});

export const ViewBs4EventPeriod = React.memo((props) => {
  const theme = useTheme();
    
  const [percentStart, percentEnd, percentWidth] = React.useMemo(() => {
    const startPeriodTime = props.startPeriod.getTime() || 1;
    const endPeriodTime = props.endPeriod.getTime() || 1;
    
    const bs4EventStartTime = Math.max(startPeriodTime, startOfDay(props.el.start).getTime());
    const bs4EventEndTime = Math.min(endPeriodTime, endOfDay(props.el.end).getTime());
  
    const percentStart = ((1.0 * (bs4EventStartTime - startPeriodTime)) / (endPeriodTime - startPeriodTime)) * 100.0;
    const percentEnd   = ((1.0 * (endPeriodTime - bs4EventEndTime)) / (endPeriodTime - startPeriodTime)) * 100.0;
    const percentWidth = ((1.0 * (bs4EventEndTime - bs4EventStartTime)) / (endPeriodTime - startPeriodTime)) * 100.0;
    return [percentStart, percentEnd, percentWidth];
  }, [props.startPeriod, props.endPeriod, props.el])    

  const [el, elArray] = React.useMemo(() => {
    if (Array.isArray(props.el.elArray)) return [null, props.el.elArray];
    return [props.el, null];
  }, [props.el])

  const sx = React.useMemo(() => {    
    const sx = props.over || props.orientation === ORIENTATION_LIST ? {
      display: 'flex',
      flexDirection: 'row',      
      overflow: 'hidden',
    } : { 
      position: 'absolute', 
      top: (el?.background || el?.bs4Event?.new) ? 0 : eventHeight * (props.line), 
      left: `${percentStart}%`, 
      right: `calc(${percentEnd}% + ${borderWidth}px)`, 
      height: (el?.background || el?.bs4Event?.new) ? '100%' : eventHeight - borderWidth,
      display: 'flex',
      flexDirection: 'row',      
      //overflow: 'hidden',
      opacity: (el?.background) ? 0.5 : 1,
      padding: 0.1,
    }    
    
    if (props.line < 0 && !(props.over || props.orientation === ORIENTATION_LIST) && !el?.background) {      
      sx.bottom = sx.top;      
      delete sx.top;
    }
    sx.userSelect = 'none';
    sx.zIndex = 10;
    sx.pointerEvents = 'auto';
    return sx;
  }, [props.line, props.over, props.orientation, percentStart, percentEnd])  

  const handleChangeView = (event) => {
    props.onChangeView(props.el.start, VIEW_TYPE_DAY);
  }

  return (  
      <Box sx={sx}>
        {el ?           
          <ViewBs4EventDraggable
            orientation={ORIENTATION_WIDTH}
            el={props.el} 
            sx={{height: sx.height, width: '100%', overflow: 'hidden'}}
            onRowClick={props.onRowClick}
            active={props.active}
            bs4Calendar={props.bs4Calendar}
          />
          : null
        }
        {elArray ? 
          <ViewBs4EventListButton        
            titleOpen={<Typography variant='subtitle2' noWrap sx={{color: '#00000077'}}>{`+${elArray.length} więcej`}</Typography>}
            title={format(props.el.start, 'iiii, d')}
            elArray={elArray}
            bs4kartotekaid={props.bs4KartotekaId}
            onRowClick={props.onRowClick}
            onChangeView={handleChangeView}
            bs4Calendar={props.bs4Calendar}
          />
          : null
        } 
      </Box>
  );
});

export const ViewBs4EventDay = React.memo((props) => {
  const theme = useTheme();

  const [percentStart, percentEnd] = React.useMemo(() => {
    // const dayStartTime = startOfDay(props.el.start).getTime() || 1;
    // const dayEndTime = endOfDay(props.el.start).getTime() || 1;

    const dayStartTime = props.day.startVisibleDay.getTime(); // startOfDay(props.el.start).getTime() || 1;
    const dayEndTime = props.day.endVisibleDay.getTime(); // endOfDay(props.el.start).getTime() || 1;
        
    const bs4EventStartTime = Math.max(dayStartTime, props.el.start.getTime());
    const bs4EventEndTime = Math.min(dayEndTime, props.el.end.getTime());
  
    const percentStart = ((1.0 * (bs4EventStartTime - dayStartTime)) / (dayEndTime - dayStartTime)) * 100.0;
    const percentEnd   = ((1.0 * (dayEndTime - bs4EventEndTime)) / (dayEndTime - dayStartTime)) * 100.0;
    return [percentStart, percentEnd];
  }, [props.day, props.el])

  const [percentWStart, percentWEnd, percentWidth] = React.useMemo(() => {
    const percentWStart = (1.0 * props.line) / (props.maxLines) * 100.0;
    // const percentWEnd = (1.0 * props.maxLines - props.line - 1) / (props.maxLines) * 100.0;
    const percentWEnd = (props.el.bs4Event?.move?.active || props.el?.bs4Event?.new) ? 0 : 8;
    const percentWidth = (1.0) / (props.maxLines) * 100.0;
    return [percentWStart, percentWEnd, percentWidth];    
  }, [props.el, props.maxLines, props.line]);

  const sx = React.useMemo(() => {    
    const sx = props.over || props.orientation === ORIENTATION_LIST ? {
      // height: eventHeight - borderWidth,
      display: 'flex',
      flexDirection: 'row',
      userSelect: 'none',
      pointerEvents: 'auto',
      // overflow: 'hidden',
    } : { 
      position: 'absolute',
      left: `${percentWStart}%`,
      right: `${percentWEnd}%`,
      top: `${percentStart}%`, 
      bottom: `${percentEnd}%`, 
      display: 'flex',
      flexDirection: 'row',
      userSelect: 'none',
      pointerEvents: 'auto',
      overflow: 'visible',
    }    
    
    return sx;
  }, [props.over, props.orientation, percentStart, percentEnd, percentWStart, percentWEnd])        

  return (  
    <Box         
      sx={sx}
      padding={'1px'}
    >         
      <ViewBs4EventDraggable 
        orientation={ORIENTATION_HEIGHT}
        el={props.el} 
        sx={{width: '100%'}} 
        onRowClick={props.onRowClick}
        bs4Calendar={props.bs4Calendar}
      />
    </Box>
);
});

const HeaderButton = styled(ButtonBase)(({ theme }) => ({ 
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: '#00000007'
  },
}));

export const ViewBs4EventListButton = (props) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.target);
  }

  const handleClose = (event) => {
    setAnchorEl(null);
  }

  const handleRowClick = (bs4Event) => {
    props.onRowClick(bs4Event);
    handleClose();
  }

  const handleChangeView = (event) => {
    props.onChangeView(event);
    handleClose();
  }

  const handlePointerDown = (event) => {
    event.stopPropagation();
  }  

  return (           
    <React.Fragment>
      <HeaderButton onClick={handleOpen} onPointerDown={handlePointerDown}>
        {props.titleOpen}
      </HeaderButton>
      <ViewBs4EventList
        {...props}        
        elArray={props.elArray} 
        anchorEl={anchorEl}
        onClose={handleClose}         
        onRowClick={handleRowClick}
        onChangeView={handleChangeView} 
        bs4Calendar={props.bs4Calendar}
      />
    </React.Fragment>
  )
}

export const ViewBs4EventList = (props) => {

  const square = 25;

  return (          
    <Bs4Popper
      anchorEl={props.anchorEl} 
      open={Boolean(props.anchorEl)} 
      onClose={props.onClose} 
      bs4kartotekaid={props.bs4KartotekaId}
      // placement='bottom'          
    >
      <Stack direction="column" spacing={0} sx={{justifyContent: "flex-start", alignItems: "flex-start",}}>
        <Stack direction="row" spacing={0} sx={{justifyContent: "space-between", alignItems: "center", width: '100%', padding: 1}}>
          <Button variant='text' onClick={props.onChangeView}> 
            <Typography>{props.title}</Typography>
          </Button>
          <IconButton
            onClick={props.onClose}
            size='small'
            sx={{
              width: square,
              height: square,
              padding: 2,
            }}              
          >
            <i className="fa-solid fa-xmark"></i>
          </IconButton>                        
        </Stack>
        <Box sx={{pl: 1, pt: 1,  pb: 1}}>
        <Stack direction="column" spacing={0.5} sx={{justifyContent: "flex-start", alignItems: "flex-start", maxHeight: '350px', overflow: 'auto', paddingRight: 1}}>
          {(props.elArray || []).map((el, index) => {
            return (              
              <ViewBs4EventDraggable
                orientation={ORIENTATION_LIST}
                key={el.bs4Event.key}
                el={el}
                onRowClick={props.onRowClick}
                // noTooltip
                onClose={props.onClose}
                tooltipPlacement='left'
                bs4Calendar={props.bs4Calendar}
              />
            )
          })}
        </Stack>
        </Box>
      </Stack>          
    </Bs4Popper>
  )
}